import { useState } from "react";
import {
  Create,
  TextInput,
  ImageField,
  ImageInput,
  FormDataConsumer,
  CheckboxGroupInput,
  BooleanInput,
  Button,
  ReferenceInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator,
  ReferenceArrayInput,
  SelectArrayInput,
  usePermissions,
  useNotify,
  TimeInput,
  NumberInput,
  useDataProvider,
  email,
} from "react-admin";
import { TabbedForm } from "../common/TabbedForm";
import {
  cuitRegex,
  validateCuit,
  validatePercentageOptional,
  validateRequired,
  validateServiceTypes,
  validateTimeOrder,
  validateTucanMenuAlias,
  validateTucanStoreId,
} from "../utils/Validations";
import { RESTAURANT_SERVICE_TYPES } from "../enums/RestaurantServiceTypes";
import { ROLE_TYPES } from "../enums/Roles";
import { Box } from "@mui/material";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { IMAGE_MAX_SIZE } from "../constants/FileConstants";
import { PAYMENT_METHODS } from "../enums/PaymentMethods";
import { useFormContext, useWatch } from "react-hook-form";
import { DEFAULT_PURCHASE_SPLIT_PERCENTAGE } from "../constants/PurchaseConstants";
import ImagePreview from "../common/ImagePreview";
import { formatTimeOnCreate, parseTimeOnCreate } from "../utils/DateTimeUtils";
import { COUNTRIES } from "../enums/Countries";
import AddressAutocomplete from "../common/AddressAutocomplete";

const BillingData = () => {
  const { setValue } = useFormContext();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [loadingBillingData, setLoadingBillingData] = useState(false);

  const resetValues = () => {
    setValue("billingData.docType", "");
    setValue("billingData.name", "");
    setValue("billingData.address", "");
    setValue("billingData.province", "");
    setValue("billingData.taxCondition", "");
  };

  const onBillingDataClick = async (formData) => {
    try {
      const cuit = formData?.billingData?.cuit;

      if (!cuitRegex.test(cuit)) {
        return notify(
          "El CUIT/CUIL no es válido. Debe tener 11 dígitos numéricos y comenzar con un prefijo válido.",
          {
            type: "error",
          }
        );
      }
      setLoadingBillingData(true);
      const {
        data: { name, address, docType, province, taxCondition },
      } = await dataProvider.getBillingDataFromCuit("billing", {
        cuit,
      });

      if (!name || !address || !docType || !province || !taxCondition) {
        notify(
          "No se ha podido obtener toda la información necesaria para autocompletar los campos de facturación. Por favor, verificar el CUIT/CUIL ingresado o completar manualmente los campos faltantes.",
          {
            type: "error",
          }
        );
        resetValues();
        return;
      }
      setValue("billingData.name", name);
      setValue("billingData.address", address);
      setValue("billingData.docType", docType);
      setValue("billingData.province", province);
      setValue("billingData.taxCondition", taxCondition);

      notify(
        "Información de facturación obtenida exitosamente y campos autocompletados.",
        { type: "success" }
      );
    } catch (error) {
      resetValues();
      notify(
        "Error al obtener los datos de facturación. Por favor, verificar el CUIT/CUIL ingresado o completar manualmente los campos.",
        {
          type: "error",
        }
      );
    } finally {
      setLoadingBillingData(false);
    }
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ flex: 0.4 }}>
              <TextInput
                label="Email"
                source="billingData.email"
                validate={[email()]}
                fullWidth
              />
              <TextInput
                label="CUIT"
                source="billingData.cuit"
                validate={validateCuit}
                fullWidth
              />
              <Button
                label="Buscar datos"
                onClick={() => onBillingDataClick(formData)}
                variant="contained"
                disabled={loadingBillingData}
                style={{
                  marginBottom: "1em",
                }}
              />
              <TextInput
                label="Tipo documento"
                source="billingData.docType"
                fullWidth
              />
              <TextInput
                label="Razón Social"
                source="billingData.name"
                fullWidth
              />
              <TextInput
                label="Domicilio fiscal"
                source="billingData.address"
                fullWidth
              />
              <TextInput
                label="Provincia"
                source="billingData.province"
                fullWidth
              />
              <TextInput
                label="Condición frente al IVA"
                source="billingData.taxCondition"
                fullWidth
              />
            </Box>
          </Box>
        </>
      )}
    </FormDataConsumer>
  );
};

const OpeningHoursRow = () => {
  // Relative source: the SimpleFormIterator's SourceContext already resolves
  // to timeTable.<i>.times.<j>
  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <TimeInput
        source="opening"
        label="Apertura"
        validate={validateRequired}
        parse={parseTimeOnCreate}
        format={formatTimeOnCreate}
        variant="outlined"
        margin="dense"
        style={{ width: 150 }}
      />
      <TimeInput
        source="closing"
        label="Cierre"
        validate={validateRequired}
        parse={parseTimeOnCreate}
        format={formatTimeOnCreate}
        variant="outlined"
        margin="dense"
        style={{ width: 150 }}
      />
    </Box>
  );
};

const ConditionalDeliveryTab = ({ permissions, ...props }) => {
  const serviceTypes = useWatch({ name: "serviceTypes" });
  if (!serviceTypes?.includes(RESTAURANT_SERVICE_TYPES.DELIVERY)) return null;
  return (
    <TabbedForm.Tab label="Delivery" {...props}>
      <NumberInput
        source="integrations.tucan.storeId"
        label="Store ID en Tucan"
        validate={validateTucanStoreId}
      />
      <TextInput
        label="Menu Alias en Tucan"
        source="integrations.tucan.menuAlias"
        validate={validateTucanMenuAlias}
      />
      {permissions === ROLE_TYPES.ADMIN && (
        <NumberInput
          source="shippingDiscountPercentage"
          label="% Descuento Envío Morfy"
          min={0}
          max={100}
          format={(v) => v * 100}
          parse={(v) => parseFloat(v) / 100}
          validate={validatePercentageOptional}
          onWheel={(e) => e.target.blur()}
          defaultValue={0}
        />
      )}
    </TabbedForm.Tab>
  );
};

const RestaurantCreate = () => {
  const { permissions } = usePermissions();
  const [country, setCountry] = useState(null);

  return (
    <Create
      title="Crear Restaurante"
      mutationMode="pessimistic"
      redirect="list"
    >
      <TabbedForm>
        <TabbedForm.Tab label="Datos básicos">
          <>
            <Box sx={{ display: "flex" }}>
              <Box sx={{ flex: 0.4, mr: "2em" }}>
                <TextInput
                  source="name"
                  label="Nombre"
                  validate={validateRequired}
                  fullWidth
                />
                <TextInput
                  multiline
                  source="description"
                  label="Descripción"
                  validate={validateRequired}
                  fullWidth
                />

                <AddressAutocomplete setCountry={setCountry} />

                <SelectInput
                  label="Tipo"
                  source="type"
                  choices={[
                    { id: RESTAURANT_TYPES.REGULAR, name: "Regular" },
                    { id: RESTAURANT_TYPES.GUIDE, name: "Guía" },
                  ]}
                />

                <BooleanInput
                  source="active"
                  label="Activo"
                  defaultValue={true}
                />

                <BooleanInput
                  source="isOnboarding"
                  label="En onboarding"
                  helperText="Permite al local entrar al backoffice aunque esté inactivo. Acordate de apagarlo cuando termine el onboarding."
                />
              </Box>
              <Box sx={{ flex: 0.4 }}>
                <ReferenceArrayInput
                  source="placeTypes"
                  reference="place-types"
                  perPage={100}
                >
                  <SelectArrayInput
                    optionText="name"
                    label="Tipos de Lugar"
                    helperText="Puedes elegir más de un tipo"
                    fullWidth
                  />
                </ReferenceArrayInput>

                <ReferenceArrayInput
                  source="placeTargets"
                  reference="place-targets"
                  perPage={100}
                >
                  <SelectArrayInput
                    optionText="name"
                    label="Target del Lugar"
                    helperText="Puedes elegir más de un target"
                    fullWidth
                  />
                </ReferenceArrayInput>

                <SelectArrayInput
                  label="Métodos de Pago (Cupones)"
                  source="couponPaymentMethods"
                  choices={[
                    {
                      id: PAYMENT_METHODS.CREDIT_CARD,
                      name: "Tarjeta de Crédito",
                    },
                    {
                      id: PAYMENT_METHODS.DEBIT_CARD,
                      name: "Tarjeta de Débito",
                    },
                    { id: PAYMENT_METHODS.MERCADO_PAGO, name: "Mercado Pago" },
                    { id: PAYMENT_METHODS.CASH, name: "Efectivo" },
                  ]}
                  validate={validateRequired}
                  fullWidth
                />

                {/* <TextInput source="menu" label="Menú (URL)" fullWidth />
                <TextInput source="instagram" label="Instagram" fullWidth />
                <TextInput source="speciality" label="Especialidad" fullWidth /> */}

                {permissions === ROLE_TYPES.ADMIN && (
                  <SelectArrayInput
                    label="Tipos de Servicio"
                    source="serviceTypes"
                    choices={[
                      { id: RESTAURANT_SERVICE_TYPES.STORE, name: "Local" },
                      {
                        id: RESTAURANT_SERVICE_TYPES.DELIVERY,
                        name: "Delivery",
                      },
                    ]}
                    validate={validateServiceTypes}
                    defaultValue={[RESTAURANT_SERVICE_TYPES.STORE]}
                    helperText="El restaurante debe tener al menos un tipo"
                    fullWidth
                  />
                )}

                {permissions === ROLE_TYPES.ADMIN && (
                  <TextInput
                    source="googlePlaceId"
                    label="Google Place ID"
                    fullWidth
                  />
                )}
              </Box>
            </Box>
          </>
        </TabbedForm.Tab>

        <TabbedForm.Tab label="Días y Horarios">
          <ArrayInput source="timeTable" label="">
            <SimpleFormIterator disableReordering>
              <FormDataConsumer>
                {() => {
                  return (
                    <>
                      <CheckboxGroupInput
                        source={"days"}
                        label="Días"
                        choices={[
                          { id: "Mo", name: "Lunes" },
                          { id: "Tu", name: "Martes" },
                          { id: "We", name: "Miércoles" },
                          { id: "Th", name: "Jueves" },
                          { id: "Fr", name: "Viernes" },
                          { id: "Sa", name: "Sábado" },
                          { id: "Su", name: "Domingo" },
                        ]}
                      />
                      <ArrayInput
                        source={"times"}
                        label=""
                        validate={validateTimeOrder}
                      >
                        <SimpleFormIterator disableReordering>
                          <OpeningHoursRow />
                        </SimpleFormIterator>
                      </ArrayInput>
                    </>
                  );
                }}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </TabbedForm.Tab>

        {permissions === ROLE_TYPES.ADMIN &&
          country &&
          COUNTRIES[country] === COUNTRIES.AR && (
            <TabbedForm.Tab label="Facturación">
              <BillingData />
            </TabbedForm.Tab>
          )}

        <TabbedForm.Tab label="Compras">
          <SelectArrayInput
            label="Métodos de Pago (Compras)"
            source="purchasePaymentMethods"
            choices={[
              {
                id: PAYMENT_METHODS.CREDIT_CARD,
                name: "Tarjeta de Crédito",
              },
              {
                id: PAYMENT_METHODS.DEBIT_CARD,
                name: "Tarjeta de Débito",
              },
              { id: PAYMENT_METHODS.MERCADO_PAGO, name: "Mercado Pago" },
            ]}
            validate={validateRequired}
          />
          {permissions === ROLE_TYPES.ADMIN && (
            <NumberInput
              source="purchaseSplitPercentage"
              label="% Fee de Morfy"
              min={0}
              max={100}
              format={(v) => v * 100}
              parse={(v) => parseFloat(v) / 100}
              validate={validatePercentageOptional}
              onWheel={(e) => e.target.blur()}
              defaultValue={DEFAULT_PURCHASE_SPLIT_PERCENTAGE}
            />
          )}
          {permissions === ROLE_TYPES.ADMIN && (
            <BooleanInput
              source="enablePurchases"
              label="Habilitar Compras"
              fullWidth
              helperText="Si está activado, reemplaza los cupones por compras en este restaurante"
              defaultValue={true}
            />
          )}
        </TabbedForm.Tab>

        <ConditionalDeliveryTab permissions={permissions} />

        <TabbedForm.Tab label="Imágenes">
          <ImageInput
            source="coverImages"
            accept={{
              "image/*": [],
            }}
            label="Imágenes de Cover"
            multiple
            maxSize={IMAGE_MAX_SIZE}
          >
            <ImagePreview source="url" />
          </ImageInput>
          <ImageInput
            source="profileImage"
            label="Imagen de Perfil"
            accept={{
              "image/*": [],
            }}
            maxSize={IMAGE_MAX_SIZE}
          >
            <ImageField source="src" title="Imagen" />
          </ImageInput>
          <FormDataConsumer>
            {({ formData, ...rest }) => {
              if (!formData.profileImage || !formData.profileImage.rawFile) {
                return <ImageField source="profileImage" {...rest} />;
              }
            }}
          </FormDataConsumer>
          <ImageInput
            source="mapImage"
            label="Imagen de Mapa"
            accept={{
              "image/*": [],
            }}
            helperText="Si no subes una imagen, se utilizará la Imagen de Perfil."
            maxSize={IMAGE_MAX_SIZE}
          >
            <ImageField source="src" title="Imagen" />
          </ImageInput>
          <FormDataConsumer>
            {({ formData, ...rest }) => {
              if (!formData.mapImage || !formData.mapImage.rawFile) {
                return <ImageField source="mapImage" {...rest} />;
              }
            }}
          </FormDataConsumer>
        </TabbedForm.Tab>

        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedForm.Tab label="Productos (Guía)">
            <ImageInput
              source="productImages"
              accept={{
                "image/*": [],
              }}
              label="Imágenes de Productos"
              multiple
              maxSize={IMAGE_MAX_SIZE}
            >
              <ImagePreview source="url" />
            </ImageInput>
          </TabbedForm.Tab>
        )}

        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedForm.Tab label="Suscripción">
            <ReferenceInput
              source="subscriptionPlan"
              reference="restaurant-subscription-plans"
              perPage={10000}
            >
              <SelectInput optionText="name" label="Plan" />
            </ReferenceInput>
          </TabbedForm.Tab>
        )}
      </TabbedForm>
    </Create>
  );
};

export default RestaurantCreate;
